import { useEffect, useState } from "react";
import { setLiveCategoryOverride } from "@/lib/categoryConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so multiple components share one fetch
let _cache = null;
let _inflight = null;
const _listeners = new Set();

// Iter 9.38 - push live admin colors/labels into the static categoryConfig
// override cache so that every static `getCategoryPreset()` consumer (38 sites)
// automatically uses live data without needing per-file refactors.
function _pushOverridesIntoStaticConfig(categories) {
  if (!categories || typeof categories !== "object") return;
  Object.entries(categories).forEach(([key, value]) => {
    setLiveCategoryOverride(key, value);
  });
}

async function _fetchOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = fetch(`${API}/categories/live-meta`)
    .then((r) => (r.ok ? r.json() : { categories: {} }))
    .then((d) => {
      _cache = d.categories || {};
      _pushOverridesIntoStaticConfig(_cache);
      _listeners.forEach((cb) => cb(_cache));
      return _cache;
    })
    .catch(() => ({}))
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

/**
 * useLiveCategoryMeta - Iter 9.35
 *
 * Replaces frontend/src/lib/categoryConfig.js as the live source for category
 * branding (label, light/dark hex, icon_url, font, hero copy, plan_verb,
 * client_noun, instructor_eligible). Falls back to static categoryConfig
 * presets if the network fetch hasn't completed yet.
 *
 * Usage:
 *   const { meta, getMeta, ready } = useLiveCategoryMeta();
 *   const m = getMeta('hair'); // {label, light_hex, dark_hex, icon_url, ...}
 *
 * Single in-flight request; multiple components share the cache.
 */
export function useLiveCategoryMeta() {
  const [meta, setMeta] = useState(_cache);
  useEffect(() => {
    let mounted = true;
    const handler = (data) => mounted && setMeta(data);
    _listeners.add(handler);
    _fetchOnce().then(handler);
    return () => {
      mounted = false;
      _listeners.delete(handler);
    };
  }, []);
  const getMeta = (categoryId) => (meta && meta[categoryId]) || null;
  return { meta: meta || {}, getMeta, ready: !!meta };
}

/**
 * Imperative refresh (e.g. after admin saves the Branding panel).
 */
export function refreshLiveCategoryMeta() {
  _cache = null;
  return _fetchOnce();
}
